import { useRef, useEffect, useState } from 'react';
import { Renderer, Program, Mesh, Geometry, OGLRenderingContext } from 'ogl';
import { logger } from "@/lib/logger";
import { throttleResizeCallback, resizeOnlyWhenChangedBy, RESIZE_CHANGE_THRESHOLD_PX } from '@/hooks/useThrottledResize';

interface WebGLRendererConfig {
  vertexShader: string;
  fragmentShader: string;
  geometry: (gl: OGLRenderingContext) => Geometry;
  uniforms: Record<string, { value: unknown }>;
  onResize?: (width: number, height: number, program: Program) => void;
  onUpdate?: (time: number, program: Program, renderer: Renderer) => void;
  alpha?: boolean;
  resolutionScale?: number;
}

interface WebGLRendererReturn {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  programRef: React.RefObject<Program | null>;
  hasError: boolean;
}

export const useWebGLRenderer = (config: WebGLRendererConfig): WebGLRendererReturn => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const programRef = useRef<Program | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const configRef = useRef(config);
  const [hasError, setHasError] = useState(false);

  // Keep configRef up to date to avoid stale closures
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const parent = canvas.parentElement;
    if (!parent) {
      return;
    }

    let renderer: Renderer | null = null;
    let frame = 0;
    let isActive = true;

    try {
      renderer = new Renderer({
        dpr: Math.min(window.devicePixelRatio, 2),
        canvas,
        alpha: config.alpha ?? false,
      });

      const gl = renderer.gl;

      if (!gl || gl.isContextLost()) {
        throw new Error('WebGL context creation failed');
      }

      // Configure alpha blending if enabled
      if (config.alpha) {
        gl.clearColor(0, 0, 0, 0);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      }

      rendererRef.current = renderer;
      const geometry = config.geometry(gl);

      const program = new Program(gl, {
        vertex: config.vertexShader,
        fragment: config.fragmentShader,
        uniforms: config.uniforms,
      });

      programRef.current = program;
      const mesh = new Mesh(gl, { geometry, program });

      const resize = () => {
        if (!isActive) return;
        const w = parent.clientWidth;
        const h = parent.clientHeight;
        const scale = config.resolutionScale ?? 1;
        renderer!.setSize(w * scale, h * scale);
        
        if (configRef.current.onResize) {
          configRef.current.onResize(w, h, program);
        }
      };
      const getSize = () => ({ width: parent.clientWidth, height: parent.clientHeight });
      const withThreshold = resizeOnlyWhenChangedBy(getSize, resize, RESIZE_CHANGE_THRESHOLD_PX);
      const throttledResize = throttleResizeCallback(withThreshold);

      window.addEventListener('resize', throttledResize);
      resize();

      const start = performance.now();

      const loop = () => {
        if (!isActive || gl.isContextLost()) {
          if (frame) cancelAnimationFrame(frame);
          return;
        }

        const time = (performance.now() - start) / 1000;

        if (configRef.current.onUpdate) {
          configRef.current.onUpdate(time, program, renderer!);
        }

        if (renderer && !gl.isContextLost()) {
          renderer.render({ scene: mesh });
          frame = requestAnimationFrame(loop);
        }
      };

      loop();

      return () => {
        isActive = false;
        cancelAnimationFrame(frame);
        window.removeEventListener('resize', throttledResize);

        if (renderer) {
          try {
            gl.getExtension('WEBGL_lose_context')?.loseContext();
          } catch (e) {
            logger.warn('Failed to lose WebGL context:', { error: e });
          }
        }
        rendererRef.current = null;
        programRef.current = null;
      };
    } catch (error) {
      logger.warn('WebGL renderer initialization failed, falling back to CSS gradient:', { error: error });
      setHasError(true);
      return;
    }
  }, [config.alpha, config.fragmentShader, config.geometry, config.uniforms, config.vertexShader, config.resolutionScale]);

  return {
    canvasRef,
    programRef,
    hasError,
  };
};
